import { useEffect, useRef } from 'react'
import { type Color } from '@backend/lib/db/typeRoots/color'
import { type LinearGradient } from '@backend/lib/db/typeRoots/layoutNode'
import { ColorPickerQuickmenu } from '@/components/ui/ColorPicker'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/shadcn/tabs'
import { GradientPicker } from './gradient-picker'
import { createDefaultGradient } from './gradient-utils'

interface FillPickerProps {
  solidColor: Color
  gradient: LinearGradient | undefined
  onSolidChange: (color: Color, isHoverSelection?: boolean) => void
  onGradientChange: (gradient: LinearGradient, isHoverSelection?: boolean) => void
  colorPalette: string[]
}

export function FillPicker({
  solidColor,
  gradient,
  onSolidChange,
  onGradientChange,
  colorPalette,
}: FillPickerProps) {
  const lastGradientRef = useRef<LinearGradient | undefined>(gradient ?? undefined)

  useEffect(() => {
    if (gradient) {
      lastGradientRef.current = gradient
    }
  }, [gradient])

  const activeTab = gradient ? 'gradient' : 'solid'

  return (
    <Tabs
      value={activeTab}
      onValueChange={(mode) => {
        if (mode === 'solid') {
          onSolidChange(solidColor)
        } else {
          onGradientChange(lastGradientRef.current ?? createDefaultGradient(solidColor))
        }
      }}
      className="min-w-[232px]">
      <TabsList className="w-full">
        <TabsTrigger value="solid" className="flex-1">
          Solid
        </TabsTrigger>
        <TabsTrigger value="gradient" className="flex-1">
          Gradient
        </TabsTrigger>
      </TabsList>

      <TabsContent value="solid">
        <ColorPickerQuickmenu
          value={solidColor}
          onChange={onSolidChange}
          colorPalette={colorPalette}
        />
      </TabsContent>

      <TabsContent value="gradient">
        {gradient && (
          <GradientPicker
            gradient={gradient}
            onChange={onGradientChange}
            colorPalette={colorPalette}
          />
        )}
      </TabsContent>
    </Tabs>
  )
}
